<template>
  <div v-if="!error" class="sm:px-4 lg:px-8 pt-4 text-sm sm:text-base">
    <NamiLoading v-if="pending" />
    <template v-else-if="post">
      <div class="space-y-4">
        <div class="container grid lg:grid-cols-[5fr_2fr] gap-4 items-start">
          <Card>
            <h1 class="text-xl sm:text-3xl font-medium" v-html="post.title" />
            <p class="mt-2 sm:mt-4">{{ new Date(post.date).toDateString() }}</p>
            <img
              class="w-full aspect-[16/9] object-cover my-6 sm:my-8"
              :src="post.cover"
            />
            <div
              class="post-content space-y-4 sm:space-y-8"
              v-html="post.content.rendered"
            />
            <ul class="flex flex-wrap gap-4 mt-6 sm:mt-8">
              <li
                v-for="tag in post.postTags"
                class="px-4 py-2 rounded-full bg-neutral-300 dark:bg-neutral-700 capitalize"
              >
                {{ tag.name }}
              </li>
            </ul>
          </Card>
          <Card>
            <h2 class="text-lg xl:text-xl font-medium">
              <TheNuxtLink :to="`/${locale}/news/${post.category}`">
                Related Articles
              </TheNuxtLink>
            </h2>
            <div class="grid gap-6 mt-6">
              <NewsCard
                v-for="categoryPost in post.otherPosts"
                :id="categoryPost.id"
                :cover="categoryPost.cover"
                :title="categoryPost.title"
                :sidebar="true"
                :slug="categoryPost.slug"
              />
            </div>
          </Card>
        </div>
        <div class="container grid lg:grid-cols-[5fr_2fr] gap-4 items-start">
          <Card>
            <h3 class="text-xl sm:text-2xl font-medium mb-4">Comments</h3>
            <ClientOnly>
              <FeedCommentsSimple :feedId="articleId" feedType="wp_news" />
            </ClientOnly>
          </Card>
        </div>
      </div>
    </template>
    <Error v-else-if="error" :error="error" @retry="refresh"
      >Post does not exist.</Error
    >
  </div>
  <ErrorPage v-else :error />
</template>

<script setup lang="ts">
import DOMPurify from "dompurify";
import {
  getTagsFromIDs,
  getPosts,
  getSingleMediaFromUrl,
  getSinglePost,
  setBaseUrl,
  type Post,
} from "~/utils/wordpress/news";
import { parse } from "node-html-parser";
import { type UserEntity } from "~/src/api";
import { ApiToNuxtError } from "~/utils/errors/404";

declare global {
  interface Window {
    twttr?: {
      widgets: {
        createTweet: (
          tweetId: string,
          element: Element,
          options?: { theme: "dark" },
        ) => Promise<void>;
      };
    };
  }
}

useHead({
  script: [
    {
      async: true,
      src: "https://platform.twitter.com/widgets.js",
    },
  ],
});

const nuxtApp = useNuxtApp();
const config = useRuntimeConfig();

const route = useRoute();
const articleId = route.params.id as string;
const { locale, t: translate } = useI18n();

const settingsStore = nuxtApp.$settings();
const authStore = nuxtApp.$auth();
const theme = computed(() => settingsStore?.settings.site.theme);
const user = computed<UserEntity | undefined>(
  () => authStore?.userEntity || undefined,
);

setBaseUrl(config.public.wpApi);

const {
  pending,
  data: post,
  error,
  refresh,
} = useAsyncData(`post-${articleId}`, async () => {
  const theWindow =
    typeof window !== "undefined"
      ? window
      : new (await import("jsdom")).JSDOM("").window;

  const { sanitize } = DOMPurify(theWindow);

  const post = await getSinglePost(Number(articleId)).catch((e) => {
    throw ApiToNuxtError(e);
  });

  post.title.rendered = sanitize(post.title.rendered);
  post.excerpt.rendered = sanitize(post.excerpt.rendered);
  post.content.rendered = sanitize(post.content.rendered);

  const [media, otherPosts] = await Promise.all([
    getSingleMediaFromUrl(post._links["wp:featuredmedia"][0].href),
    getPosts({ per_page: 6, categories: post.categories }),
  ]);

  otherPosts.forEach((newsItem) => {
    newsItem.title.rendered = sanitize(newsItem.title.rendered);
    newsItem.excerpt.rendered = sanitize(newsItem.excerpt.rendered);
  });

  const newsMediaIDMap = new Map<string, Post[]>();

  for (const newsItem of otherPosts) {
    const link = newsItem._links["wp:featuredmedia"][0].href;

    if (newsMediaIDMap.get(link)) {
      newsMediaIDMap.get(link)?.push(newsItem);
    } else {
      newsMediaIDMap.set(link, [newsItem]);
    }
  }

  const otherMedia = await Promise.all(
    Array.from(newsMediaIDMap.entries()).map(async ([link, post]) => ({
      postIds: post.map((p) => p.id),
      cover: await getSingleMediaFromUrl(link),
    })),
  );

  return {
    post: post,
    media: media,
    cover: media.media_details.sizes.full.source_url,
    title: post.title.rendered,
    date: post.date,
    content: post.content,
    category: post.categories[0],
    otherPosts: otherPosts.map((post) => ({
      id: post.id,
      cover: otherMedia.filter((cover) => cover.postIds.includes(post.id))[0]
        .cover.media_details.sizes.full.source_url,
      title: post.title.rendered,
      excerpt: post.excerpt.rendered,
      slug: post.slug,
    })),
    postTags: await getTagsFromIDs(post.tags),
  };
});

use404Response(error);

const seo = computed(() =>
  post.value
    ? {
        title: parse(post.value.post.title.rendered).textContent,
        description: parse(post.value.post.excerpt.rendered).textContent,
        slug: post.value.post.slug,
        coverUrl: post.value.media.media_details.sizes.full.source_url,
        href: `${config.public.baseUrl}/n/${articleId}`,
      }
    : {},
);

useServerSeoMeta({
  title: () => seo.value.title ?? "",
  ogTitle: () => seo.value.title,
  twitterTitle: () => seo.value.title,
  description: () => seo.value.description,
  ogDescription: () => seo.value.description,
  twitterDescription: () => seo.value.description,
  ogUrl: () => seo.value.href,
  ogLocale: () => locale.value,
  ogImage: () => seo.value.coverUrl,
  ogImageSecureUrl: () => seo.value.coverUrl,
  twitterImage: () => seo.value.coverUrl,
  ogImageHeight: "1200",
  ogImageWidth: "630",
  ogImageUrl: () => seo.value.coverUrl,
  ogImageType: "image/png",
  ogImageAlt: "NamiComi",
  ogLocaleAlternate: "en_GB",
});

watch(
  post,
  async (v) => {
    await (async () => new Promise<void>((r) => setTimeout(r, 200)))();

    if (!v || !window?.twttr || !document) {
      return;
    }

    Array.from(document.querySelectorAll(".post-content p a")).forEach((el) => {
      el.classList.add("text-nami-nami-blue");
      el.classList.add("hover:underline");
    });

    Array.from(document.querySelectorAll(".post-content p img")).forEach(
      (el) => {
        el.classList.add("mx-auto");
      },
    );

    Array.from(document.querySelectorAll(".post-content figure")).forEach(
      (el) => {
        if (!el || !(el instanceof HTMLElement)) return;

        el.classList.add("mx-auto");
        el.classList.add("max-w-fit");
        el.style.width = "";
      },
    );

    Array.from(document.querySelectorAll(".post-content p")).forEach((el) => {
      const url = el.textContent;

      if (url?.match(/youtube/)) {
        const youtubeId = url
          .match(/(.*?)(^|\/|v=)([a-z0-9_-]{11})(.*)?/im)
          ?.at(3);

        if (!youtubeId) {
          return;
        }

        el.textContent = "";
        const fragment = document.createDocumentFragment();
        const iframe = fragment.appendChild(document.createElement("iframe"));
        iframe.id = "ytplayer";
        iframe.src = `https://www.youtube.com/embed/${youtubeId}`;
        iframe.frameBorder = "0";
        iframe.classList.add("min-w-full");
        iframe.classList.add("aspect-video");

        el.appendChild(fragment);
      } else if (url?.match(/twitter/) || url?.match(/x\.com/)) {
        const tweetId = url.match(/\/status\/(\d+)/)?.at(1);

        if (!tweetId) {
          return;
        }

        el.textContent = "";
        const div = el.appendChild(document.createElement("div"));
        div.classList.add("flex");
        div.classList.add("justify-center");
        div.classList.add("w-full");

        if (theme.value === "light") {
          window.twttr!.widgets.createTweet(tweetId, div).then(function () {
            console.log(`Tweet ID ${tweetId} added.`);
          });
        } else {
          window
            .twttr!.widgets.createTweet(tweetId, div, { theme: "dark" })
            .then(function () {
              console.log(`Tweet ID ${tweetId} added.`);
              console.log(
                div.children[0].children[0].classList.add("rounded-xl"),
              );
            });
        }
      }
    });
  },
  { immediate: true },
);
</script>
